import React from 'react'
import Card from 'organization/Event/AreaList/Card'
import AreasProvider, {Area, useAreas} from 'organization/Event/AreasProvider'
import {useEventRoutes} from 'organization/Event/EventRoutes'
import Page from 'organization/Event/Page'
import {
  CHECK_IN_ATTENDEES,
  EVENT_SETTINGS,
  START_AREAS,
  START_ROOMS,
  usePermissions,
} from 'organization/PermissionsProvider'
import PageHeader from 'lib/ui/PageHeader'
import Title from 'lib/ui/PageHeader/Title'
import HasPermission from 'organization/HasPermission'
import {RelativeLink} from 'lib/ui/link/RelativeLink'
import NewItemButton from 'lib/ui/PageHeader/NewItemButton'
import {StaticAreaProvider} from 'organization/Event/Area/AreaProvider'
import {ZoomAreasBreadcrumbs} from 'organization/Event/Page/PageBreadcrumbs'
import FullPageLoader from 'lib/ui/layout/FullPageLoader'
import styled from 'styled-components'
import Button from 'lib/ui/PageHeader/PageHeaderButton'

export default function AreaList() {
  return (
    <ZoomAreasBreadcrumbs page="Zoom Areas">
      <Page>
        <AreasProvider>
          <Content />
        </AreasProvider>
      </Page>
    </ZoomAreasBreadcrumbs>
  )
}

function Content() {
  const routes = useEventRoutes()

  return (
    <>
      <StyledPageHeader>
        <Title text="Zoom Areas" />
        <HasPermission required={EVENT_SETTINGS}>
          <RightButtons>
            <RelativeLink disableStyles to={routes.areas.zoom_user_settings}>
              <Button
                text="Zoom User Settings"
                aria-label="zoom user settings"
                variant="outlined"
                color="primary"
              />
            </RelativeLink>
            <RelativeLink disableStyles to={routes.areas.create}>
              <NewItemButton text="New Area" aria-label="create area" />
            </RelativeLink>
          </RightButtons>
        </HasPermission>
      </StyledPageHeader>

      <Areas />
    </>
  )
}

function Areas() {
  const {areas, loading} = useAreas()
  const {can} = usePermissions()

  if (loading || !areas) {
    return <FullPageLoader />
  }

  const visibleAreas = areas.filter((a) => {
    if (can(START_ROOMS) || can(EVENT_SETTINGS) || can(START_AREAS)) {
      return true
    }

    /**
     * If a user only has permission to check in attendees (tech check agent), then
     * we'll only show the tech check area.
     */
    if (can(CHECK_IN_ATTENDEES)) {
      return a.is_tech_check
    }

    return false
  })

  return (
    <div>
      <List areas={visibleAreas} />
    </div>
  )
}

function List(props: {areas: Area[]}) {
  const {areas} = props

  const isEmpty = areas.length === 0
  if (isEmpty) {
    return <div>No areas have been created</div>
  }

  return (
    <>
      {areas.map((a) => (
        <StaticAreaProvider area={a} key={a.id}>
          <Card />
        </StaticAreaProvider>
      ))}
    </>
  )
}

const StyledPageHeader = styled(PageHeader)`
  margin-bottom: ${(props) => props.theme.spacing[2]} !important;

  @media (min-width: ${(props) => props.theme.breakpoints.sm}) {
    margin-bottom: ${(props) => props.theme.spacing[3]} !important;
  }
`

const RightButtons = styled.div`
  display: flex;
  gap: ${(props) => props.theme.spacing[2]};
`
